import React, { useState } from 'react';
import {
  Avatar, Box, Button, Chip, Divider, Grid, List, ListItem, ListItemAvatar,
  ListItemText, Rating, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import { Block, CancelOutlined, CheckCircleOutline, LockOpen } from '@mui/icons-material';
import TelegramIcon from '@mui/icons-material/Telegram';
import { BaseDialog } from '../common/BaseDialog';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { AdminExpertDetail, ConnectionStatus, TelegramStatus, adminUsersService } from '../../services/admin-users.service';
import { ImpersonateButton } from './ImpersonateButton';
import { UserStatusChip } from './UserStatusChip';
import { toastService } from '../../services/toast.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface Props {
  expert: AdminExpertDetail | null;
  open: boolean;
  onClose: () => void;
  onStatusChanged?: (userId: number, status: number) => void;
}

const GENDER_LABEL: Record<string, string> = {
  male: 'Чоловіча',
  female: 'Жіноча',
};

const CONNECTION_STATUS: Record<ConnectionStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active:     { label: 'Активний',  color: 'success' },
  pending:    { label: 'Очікує',    color: 'warning' },
  rejected:   { label: 'Відхилено', color: 'error'   },
  terminated: { label: 'Завершено', color: 'default' },
};

const TELEGRAM_STATUS_CFG: Record<TelegramStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active:        { label: 'Підключено', color: 'success' },
  pending:       { label: 'Очікує',    color: 'warning' },
  blocked:       { label: 'Заблоковано', color: 'error'   },
  NOT_CONNECTED: { label: 'Не підключено', color: 'default' },
};

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value ?? '—'}</Typography>
  </Grid>
);

export const AdminExpertDetailModal: React.FC<Props> = ({ expert, open, onClose, onStatusChanged }) => {
  const [tab, setTab] = useState(0);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);

  const handleClose = () => { setTab(0); onClose(); };

  const handleConfirmStatusToggle = async () => {
    if (!expert) return;
    const newStatus = expert.status === 1 ? 2 : 1;
    try {
      await adminUsersService.setUserStatus('experts', expert.id, newStatus);
      onStatusChanged?.(expert.id, newStatus);
      toastService.success(newStatus === 2 ? 'Експерта заблоковано.' : 'Експерта розблоковано.');
    } catch {
      toastService.error('Не вдалося змінити статус експерта.');
    } finally {
      setStatusConfirmOpen(false);
    }
  };

  if (!expert) return null;

  const fullName = [expert.name, expert.surname].filter(Boolean).join(' ');
  const clientsCount = expert.connectedClients.length;

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar src={expert.avatar} sx={{ width: 40, height: 40 }}>
        {expert.name.charAt(0)}
      </Avatar>
      <Box>
        <Typography variant="h6" lineHeight={1.2}>{fullName}</Typography>
        <Typography variant="body2" color="text.secondary">{expert.email}</Typography>
      </Box>
    </Box>
  );

  const tgCfg = TELEGRAM_STATUS_CFG[expert.telegramStatus || 'NOT_CONNECTED'];

  return (
    <>
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="md"
      actions={
        <>
          {expert.status !== 3 && (
            <Button
              color={expert.status === 1 ? 'error' : 'success'}
              startIcon={expert.status === 1 ? <Block /> : <LockOpen />}
              onClick={() => setStatusConfirmOpen(true)}
            >
              {expert.status === 1 ? 'Заблокувати' : 'Розблокувати'}
            </Button>
          )}
          <ImpersonateButton userId={expert.id} userName={fullName} onSuccess={handleClose} />
        </>
      }
      data-i-doc={ModalIndices.ADMIN_EXPERT_DETAIL}
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, mt: -1 }}>
        <Tab label="Профіль" />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Клієнти
              {clientsCount > 0 && (
                <Chip label={clientsCount} size="small" color="primary" sx={{ height: 18, fontSize: 11 }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {tab === 0 && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Основна інформація
          </Typography>
          <Grid container spacing={2}>
            <Row label="ID" value={expert.id} />
            <Row label="Ім'я" value={expert.name} />
            <Row label="Прізвище" value={expert.surname} />
            <Row label="Телефон" value={expert.phone} />
            <Row label="Стать" value={expert.gender ? GENDER_LABEL[expert.gender] : undefined} />
            <Row label="Рік народження" value={expert.bornYear} />
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Typography variant="body1">{expert.email}</Typography>
                {expert.isEmailVerified ? (
                  <Tooltip title="Верифіковано"><CheckCircleOutline color="success" sx={{ fontSize: 16 }} /></Tooltip>
                ) : (
                  <Tooltip title="Не верифіковано"><CancelOutlined color="disabled" sx={{ fontSize: 16 }} /></Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Статус акаунту</Typography>
              <Box sx={{ mt: 0.5 }}>
                <UserStatusChip status={expert.status} isProtectedDemoAccount={expert.isProtectedDemoAccount} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Telegram</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <TelegramIcon color={tgCfg.color === 'default' ? 'disabled' : (tgCfg.color as any)} />
                <Chip
                  label={tgCfg.label}
                  color={tgCfg.color}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Профіль експерта
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Рейтинг</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Rating value={expert.rating} precision={0.1} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">{expert.rating != null ? expert.rating.toFixed(1) : '—'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Спеціалізації</Typography>
              {expert.specializations.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {expert.specializations.map((s) => (
                    <Chip key={s} label={s} size="small" variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body1">—</Typography>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Активність
          </Typography>
          <Grid container spacing={2}>
            <Row
              label="Останній вхід"
              value={expert.lastLoginAt ? new Date(expert.lastLoginAt).toLocaleString('uk-UA') : undefined}
            />
            <Row
              label="Дата реєстрації"
              value={new Date(expert.createdAt).toLocaleString('uk-UA')}
            />
          </Grid>
        </>
      )}

      {tab === 1 && (
        clientsCount === 0 ? (
          <Typography variant="body2" color="text.secondary">Немає підключених клієнтів</Typography>
        ) : (
          <List dense disablePadding>
            {expert.connectedClients.map((c) => {
              const statusCfg = CONNECTION_STATUS[c.connectionStatus];
              return (
                <ListItem key={c.id} disableGutters>
                  <ListItemAvatar>
                    <Avatar src={c.avatar} sx={{ width: 32, height: 32 }}>
                      {c.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={`${c.name} ${c.surname}`} />
                  <Chip label={statusCfg.label} color={statusCfg.color} size="small" variant="outlined" />
                </ListItem>
              );
            })}
          </List>
        )
      )}
    </BaseDialog>

    <ConfirmationDialog
      open={statusConfirmOpen}
      onClose={() => setStatusConfirmOpen(false)}
      onConfirm={handleConfirmStatusToggle}
      title={expert.status === 1 ? 'Заблокувати експерта?' : 'Розблокувати експерта?'}
      message={
        expert.status === 1
          ? `${fullName} втратить доступ до застосунку. Дані не видаляються — дію можна скасувати.`
          : `${fullName} знову зможе увійти в застосунок.`
      }
      confirmText={expert.status === 1 ? 'Заблокувати' : 'Розблокувати'}
    />
    </>
  );
};
